import type { Action, ThunkAction } from '@reduxjs/toolkit';
import { combineSlices, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/es/storage';
import { questionsSlice } from './slices/questionsSlice';
import { answersSlice } from './slices/answersSlice';
import { exploreSlice } from './slices/exploreSlice';
import { layoutSlice } from './slices/layoutSlice';
import { userSlice } from './slices/userSlice';
import { gameParrotSlice } from './slices/gameParrotSlice';

/**
 * Combines reducers from all slices.
 */
const rootReducer = combineSlices(
  questionsSlice,
  answersSlice,
  exploreSlice,
  layoutSlice,
  userSlice,
  gameParrotSlice,
);

// Persist configuration
const persistConfig = {
  key: 'root', // Root key for the persisted state
  storage, // Use localStorage
  whitelist: ['user', 'layout'], // Only persist user and layout slices
};

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure the store with the persisted reducer
export const makeStore = () =>
  configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: false, // Disable serializable check for redux-persist
      }),
  });

// Export the persistor
export const persistor = persistStore(makeStore());

/**
 * Represents the type of the rootReducer.
 */
export type RootState = ReturnType<typeof rootReducer>;

/**
 * Represents the type of the redux store.
 */
export type AppStore = ReturnType<typeof makeStore>;

/**
 * Represents the type of a dispatch action.
 */
export type AppDispatch = AppStore['dispatch'];

/**
 * Represents the type of a thunk action.
 */
export type AppThunk<ThunkReturnType = void> = ThunkAction<
  ThunkReturnType,
  RootState,
  unknown,
  Action
>;
